import { COMMENT_MODEL_CLASS_NAME } from 'models/daos/comment/constants'
import { REPORT_MODEL_CLASS_NAME } from 'models/daos/report/constants'
import REDUX_ASSOCIATION from 'modules/redux/constants/association'

import {
  REDUX_CLASS_NAME_TO_ORDER,
  REDUX_CLASS_NAME_TO_SORT_KEY,
  REDUX_DEFAULT_DOMAIN_LIST_KEY,
  REDUX_DEFAULT_ORDER,
  REDUX_DEFAULT_SORT_KEY,
  REDUX_ORDER
} from 'modules/redux/constants'
import { MODEL_KEY } from 'modules/redux/constants/model-key'
import getAssociationKeysReduxUtil from 'modules/redux/utils/get-associations-keys'
import getDefaultDomainListPathFromPath from 'modules/redux/utils/get-default-domain-list-path-from-domain-path'
import { getDomainMapValueReduxUtil } from 'modules/redux/utils/get-domain-map-value'
import getDomainPathFromPathReduxUtil from 'modules/redux/utils/get-domain-path-from-path'
import { getInstanceReduxUtil } from 'modules/redux/utils/get-instance'
import getPathReduxUtil from 'modules/redux/utils/get-path'
import isInstanceReduxUtil from 'modules/redux/utils/is-instance'
import isNewReduxUtil from 'modules/redux/utils/is-new'
import { createPointerReduxUtil } from 'modules/redux/utils/legacy/create'
import { delReduxUtil } from 'modules/redux/utils/legacy/del'
import { getDomainPathReduxUtil } from 'modules/redux/utils/legacy/get-domain-path'
import getInstancesFromDomainMapReduxUtil from 'modules/redux/utils/legacy/get-instances-from-domain-map'
import { getSectionDomainMapValueListPathReduxUtil } from 'modules/redux/utils/legacy/get-section-domain-map-value-list-path'
import { isPointerReduxUtil } from 'modules/redux/utils/legacy/is-pointer'
import { setReduxUtil } from 'modules/redux/utils/legacy/set'
import { updateDataReduxUtil } from 'modules/redux/utils/legacy/update-data'
import {
  getObjectUtil,
  invertObjectUtil,
  logUtil,
  uniqArrayUtil
} from 'packages/src'
import sortByArrayUtil from 'packages/src/modules/array/sort-by/index.js'

/**
 * Gets the `sortKey` of a given instance.
 * `REDUX_DEFAULT_SORT_KEY` is used as default.
 * @private
 * @param  {Instance} instance
 * @returns {string}
 */
function getSortKeyReduxUtil (instance) {
  return (
    REDUX_CLASS_NAME_TO_SORT_KEY[instance.className] ||
    REDUX_DEFAULT_SORT_KEY
  )
}

/**
 * Gets the `order` of a given instance.
 * `REDUX_DEFAULT_ORDER` is used as default.
 * @private
 * @param  {Instance} instance
 * @returns {string}
 */
function getOrderReduxUtil (instance) {
  return (
    REDUX_CLASS_NAME_TO_ORDER[instance.className] ||
    REDUX_DEFAULT_ORDER
  )
}

/**
 * Gets the instances by the given key.
 * @param  {Instance[]} instances
 * @returns {String[]}
 */
function getDomainMapValuesReduxUtil (instances) {
  return instances.map(instance => getDomainMapValueReduxUtil(instance))
}

/**
 * A mapping of className to `DomainList` path function.
 * @private
 * @type {Object}
 */
const CLASS_NAME_TO_DOMAIN_LIST_PATH_GETTER_FUNCTION = {
  // [CATEGORY_MODEL_CLASS_NAME] : getSectionDomainListPath,
  // [PRODUCT_MODEL_CLASS_NAME]  : getSectionDomainListPath,
  [COMMENT_MODEL_CLASS_NAME]: getSectionDomainMapValueListPathReduxUtil,
  [REPORT_MODEL_CLASS_NAME]: getSectionDomainMapValueListPathReduxUtil
}

/**
 * Gets a `DomainList` path according to the given `Instance`.
 * @private
 * @param  {string} path
 * @param  {Instance} instance
 * @param  {Instance} association
 * @returns {string}
 */
function getInstanceDomainListPathReduxUtil (path, instance, association) {
  let domainListPath
  const getter = CLASS_NAME_TO_DOMAIN_LIST_PATH_GETTER_FUNCTION[association.className]
  if (getter) {
    domainListPath = getter(path, instance)
  }
  return domainListPath || `${path}.${REDUX_DEFAULT_DOMAIN_LIST_KEY}`
}

/**
 * Gets the associations of the given instance
 * and put forEach instances in their respective domain.
 *
 * @param  {Object} state
 * @param  {string} path
 * @param  {Instance} instance
 * @returns {Object}
 */
function normalizeReduxUtil (state, path, instance) {
  // Get the keys
  const associationKeys = getAssociationKeysReduxUtil(instance)

  // There is no associationKeys
  if (!associationKeys.length) {
    return state
  }

  // Go through all the associationKeys and create instances
  associationKeys.forEach(associationKey => {
    const association = instance.data[associationKey]
    // Data doesn't contain any association data
    if (!association) {
      return
    }
    if (isPointerReduxUtil(association)) {
      return
    }
    // If it's an array of instances,
    // add forEach instance into their domain
    if (Array.isArray(association)) {
      state = association.reduce((updatedState, singleAssociation, index) => {
        if (isPointerReduxUtil(singleAssociation)) {
          return updatedState
        }

        // Convert each instance into a pointer
        const pointer = createPointerReduxUtil(singleAssociation)
        instance = setReduxUtil(
          instance,
          `data.${associationKey}.${index}`,
          pointer
        )
        // Add the instance into its Domain/SubDomain
        const instancePath = getDomainPathReduxUtil(
          singleAssociation.className,
          instance
        )
        // Get the specific `DomainList` path if it has one
        const domainListPath = getInstanceDomainListPathReduxUtil(
          instancePath,
          instance,
          singleAssociation
        )

        return addReduxUtil({
          domainListPath,
          instance: singleAssociation,
          path: instancePath,
          state: updatedState
        })
      }, state)
    } else {
      // If it's a instance, add it into its domain
      if (!isPointerReduxUtil(association)) {
        // Convert the association into a pointer
        instance = updateDataReduxUtil(instance, {
          [associationKey]: createPointerReduxUtil(association)
        })
      }

      // If the association was not already added, add it
      if (!getInstanceReduxUtil(state, association)) {
        // Add the instance into its domain
        state = addReduxUtil({
          state,
          path: getDomainPathReduxUtil(association.className),
          instance: association
        })
      }
    }
  })
  return setReduxUtil(state, getPathReduxUtil(instance), instance)
}

/**
 * Merges the `newInstance` with the existing instance.
 * If the instance doesn't exist, it simply sets it.
 * @private
 * @param  {Object} state
 * @param  {string} path
 * @param  {Instance} newInstance
 * @returns {Object}
 */
function mergeAndSetReduxUtil (state, path, newInstance) {
  // The instance doesn't exist, set it
  let instance = getObjectUtil(state, path)
  if (!instance || !instance.data) {
    return setReduxUtil(state, path, newInstance)
  }
  // The instance exists, merge it with the given one
  instance = mergeReduxUtil(instance, newInstance)
  return setReduxUtil(state, path, instance)
}

function mergeReduxUtil (instance, newInstance) {
  Object.keys(newInstance.data)
    .forEach((key) => {
      const value = newInstance.data[key]
      if (value !== instance.data[key]) {
        instance = updateDataReduxUtil(instance, {
          [key]: value
        })
      }
    })
  const className = instance.className || newInstance.className
  return {
    ...instance,
    className,
    status: newInstance.status,
    changed: newInstance.changed
  }
}

/**
 * Adds a instance from the given path.
 * NOTE: if the instance is in a DomainMap, it's also added to the DomainList.
 * @param  {Object}  data
 *         {Object}   state
 *         {string}  path
 *         {Instance}   instance
 *         {string}  [associatedPath]
 *         {string}  [domainListPath]
 *         {Object}  [options={}]
 *         {boolean} [REDUX_OPTION.SHALLOW_PARSING]
 * @returns {Object}
 */
function addReduxUtil (data) {
  let {
    state,
    associatedPath,
    path,
    instance,
    domainListPath
  } = data

  // Domain not in the store yet
  if (!path) {
    return state
  }

  // Make sure to use `undefined`, if `null` is specified
  // it explicitly means the instance should not be added to a `DomainList`
  // NOTE: often use for a save on an existing instance
  if (domainListPath === undefined) {
    domainListPath = getDefaultDomainListPathFromPath(path)
  }
  // Get the current instance
  const currentInstance = getObjectUtil(state, path)

  // Delete the old instance if necessary and update the editing domain
  if (isInstanceReduxUtil(currentInstance)) {
    // If the instance is new, delete the instance at the old path
    if (isNewReduxUtil(currentInstance)) {
      state = delReduxUtil(state, path, undefined)
    }
  }

  // Merge and set the instance
  const instancePath = getPathReduxUtil(instance) || path
  state = mergeAndSetReduxUtil(state, instancePath, instance)

  // Get the updated instance
  instance = getObjectUtil(state, instancePath)

  // Add the instance to the DomainList
  if (domainListPath) {
    state = addToDomainListReduxUtil({
      state,
      path: (
        isInstanceReduxUtil(currentInstance)
          ? getDomainPathFromPathReduxUtil(path)
          : path
      ),
      associatedPath,
      instance,
      domainListPath
    })
  }

  // Add the instance inside the associated instance
  if (associatedPath) {
    const associatedInstance = getObjectUtil(state, associatedPath)
    const association = REDUX_ASSOCIATION[associatedInstance.className]
    const instanceKey = invertObjectUtil(association)[instance.className]
    state = mergeAndSetReduxUtil(
      state,
      `${associatedPath}.data.${instanceKey}`,
      createPointerReduxUtil(instance)
    )
  }

  // If it's a shallow parsing, add the given path to the `parser` domain
  // so it can be parsed by the client
  // TODO: shallow parsing is not really happening,
  // return `addToParser(state, path)` to enable
  // if (options[REDUX_OPTION.SHALLOW_PARSING]) {
  // }
  // state = addToParser(state, instancePath)

  return normalizeReduxUtil(state, path, instance)
}

/**
 * Gets the ordered instances.
 * @private
 * @param  {Object} data
 *         {Instance[]} instances
 *         {string} [sortKey]
 *         {REDUX_ORDERS} [order=DEFAULT_ORDER]
 * @returns {instance[]}
 */
function getSortedInstancesReduxUtil (data) {
  const {
    instances,
    order = REDUX_DEFAULT_ORDER,
    sortKey = REDUX_DEFAULT_SORT_KEY
  } = data
  const orderBy = order === REDUX_ORDER.ASC
    ? 1
    : -1
  return sortByArrayUtil(instances, instance => {
    let value = instance.data[sortKey]
    if ([
      MODEL_KEY.CREATED_AT,
      MODEL_KEY.UPDATED_AT
    ].includes(sortKey)) {
      value = (
        new Date(value)
      ).getTime()
    }
    if (typeof value === 'string') {
      return value.toLowerCase()
    }
    return value * orderBy
  })
}

// const PARSER_PATH_DATA = `${REDUX_DOMAIN.PARSER}.data`
//
// function getParserData (state) {
//   return getObjectUtil(state, PARSER_PATH_DATA)
// }
//
// function isInParser (state, path) {
//   return !!getParserData(state)[path]
// }

/**
 * Adds the given `Instance`'s path to the `state.parser` data.
 * NOTE: Used to fasten server-side rendering.
 * @private
 * @param  {Object} state
 * @param  {string} path
 * @returns {Object}
 */
// function addToParser (state, path) {
//   if (isInParser(state, path)) {
//     return state
//   }
//   return setReduxUtil(
//     state,
//     PARSER_PATH_DATA,
//     {
//       ...getParserData(state),
//       [path]: true
//     }
//   )
// }

/**
 * Adds a instance to the DomainList(s).
 * NOTE: If the DomainList key is `all`, the instance will also be added to
 * the categorized DomainLists.
 * e.g. `state.articles.${category}` => `state.articles.nutrition`
 * @private
 * @param {Object} data
 *        {Object} state
 *        {string} path
 *        {Instance} instance
 *        {string} domainListPath
 *        {number} [categoryIndex] Index used to handle multiple DomainList(s)
 *                                 for a single instance when originally added
 *   to the `all` DomainList.
 * @returns {Object}
 */
function addToDomainListReduxUtil (data) {
  let {
    state,
    path,
    instance,
    domainListPath
  } = data
  // Get the DomainList
  let domainList = getObjectUtil(state, domainListPath)
  let domainListData = getObjectUtil(domainList, 'data') || []
  // NOTE: these instances will be a list of keys,
  // when all of them are loaded
  // @see loadSuccessArray/loadSuccessSubDomain
  let domain = getObjectUtil(state, path)
  if (!domain) {
    logUtil('addToDomainListReduxUtil').warn(`path=${path} domain not found instance=`, instance)
    domain = {
      data: {}
    }
  }

  let instances = getInstancesFromDomainMapReduxUtil(
    domain.data,
    domainListData
  )
  // Add the instance to the DomainList
  instances = [
    ...instances,
    instance
  ]
  const sortKey = getSortKeyReduxUtil(instance)
  const order = getOrderReduxUtil(instance)
  instances = getSortedInstancesReduxUtil({
    instances,
    order,
    sortKey
  })
  // Get the instance keys
  domainListData = getDomainMapValuesReduxUtil(instances)
  // Add the instances to the DomainList
  domainList = setReduxUtil(domainList, 'data', uniqArrayUtil(domainListData))
  // Update the DomainList
  state = setReduxUtil(state, domainListPath, domainList)
  return state
}

export {
  addReduxUtil
}
